<template>
  <div class="w-full space-y-4 text-slate-100">
    <!-- TOP BAR -->
    <div class="border border-slate-700 bg-slate-800 bg-opacity-80 shadow-lg pageShell">
      <div class="flex flex-col gap-4 px-4 py-4 sm:px-5 sm:py-5">
        <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <!-- LEFT -->
          <div class="min-w-0">
            <nav class="hidden sm:flex" aria-label="Breadcrumb">
              <ol role="list" class="flex items-center gap-2 text-xs text-slate-400">
                <li class="flex items-center">
                  <router-link
                    to="/hub"
                    class="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-slate-600 bg-slate-700 bg-opacity-70 text-slate-200 transition hover:bg-slate-600"
                  >
                    <HomeIcon class="h-4 w-4" aria-hidden="true" />
                    <span class="sr-only">Home</span>
                  </router-link>
                </li>

                <li class="flex items-center gap-2">
                  <ChevronRightIcon class="h-4 w-4 text-slate-500" />
                  <router-link
                    to="/dashboard/routes"
                    class="font-medium text-slate-300 transition hover:text-white"
                  >
                    Trasy
                  </router-link>
                </li>

                <li class="flex items-center gap-2">
                  <ChevronRightIcon class="h-4 w-4 text-slate-500" />
                  <span class="font-medium text-slate-300">Tworzenie trasy</span>
                </li>
              </ol>
            </nav>

            <div class="flex items-center sm:hidden">
              <router-link
                to="/dashboard/routes"
                class="inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-700 px-3 py-2 text-sm font-medium text-slate-200 transition hover:bg-slate-600"
              >
                <ArrowLeftIcon class="h-4 w-4" />
                Cofnij
              </router-link>
            </div>

            <div class="mt-3">
              <div class="text-lg font-semibold tracking-tight text-white">
                Tworzenie nowej trasy
              </div>
              <div class="mt-1 text-xs text-slate-400">
                Skonfiguruj podstawowe dane trasy, przypisz zamówienia i uzupełnij końcowe informacje.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- PROGRESS -->
    <div class="border border-slate-700 bg-slate-800 bg-opacity-80 shadow-lg pageShell">
      <div class="px-4 py-4 sm:px-5">
        <div class="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div class="text-xs font-semibold uppercase tracking-wide text-slate-300">
              Progres dodawania trasy
            </div>
            <div class="mt-1 text-xs text-slate-400">
              Krok {{ step }} z 3
            </div>
          </div>

          <div class="flex flex-wrap items-center gap-2">
            <span :class="['stepPill', step >= 1 ? 'stepPillActive' : '']">1. Podstawy</span>
            <span :class="['stepPill', step >= 2 ? 'stepPillActive' : '']">2. Zamówienia</span>
            <span :class="['stepPill', step >= 3 ? 'stepPillActive' : '']">3. Finalizacja</span>
          </div>
        </div>

        <div class="mt-4 overflow-hidden rounded-full bg-slate-700">
          <div
            class="h-2 rounded-full bg-gradient-to-r from-purple-400 via-indigo-400 to-blue-500 transition-all duration-700"
            :style="`width: ${progress}%`"
          />
        </div>
      </div>
    </div>

    <!-- STEP 1 -->
    <div v-show="step === 1" class="space-y-4">
      <!-- MAIN GRID: 4 cards -->
      <div class="grid grid-cols-1 gap-4 xl:grid-cols-2">
        <!-- DATE -->
        <section class="sectionCard">
          <div class="sectionHead">
            <div>
              <div class="sectionTitle">Termin trasy</div>
              <div class="sectionSub">
                Wybierz planowaną datę i godzinę wyjazdu oraz powrotu.
              </div>
            </div>
          </div>

          <div class="sectionBody">
            <div class="datePickerWrap">
              <VueDatePicker
                placeholder="Wybierz datę i godzinę wyjazdu..."
                partial-flow
                :flow="['calendar', 'time']"
                :is-24="true"
                locale="pl"
                v-model="date"
                range
                :min-date="todayAt00()"
                :format="periodDateFormat"
                cancelText="Anuluj"
                selectText="Wybierz"
              />
            </div>
          </div>
        </section>

        <!-- DRIVER -->
        <section class="sectionCard">
          <div class="sectionHead">
            <div>
              <div class="sectionTitle">Kierowca</div>
              <div class="sectionSub">
                Wybierz kierowcę przypisanego do realizacji trasy.
              </div>
            </div>
          </div>

          <div class="sectionBody">
            <Listbox as="div" v-model="selectedDriver" v-if="drivers.all.length > 0 && !load">
              <div class="relative">
                <ListboxButton class="listboxButton">
                  <span class="block truncate">
                    {{ selectedDriver === null ? 'Wybierz kierowcę...' : (selectedDriver.name + ' ' + selectedDriver.surname) }}
                  </span>

                  <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <ChevronDownIcon class="h-5 w-5 text-slate-400" aria-hidden="true" />
                  </span>
                </ListboxButton>

                <transition
                  leave-active-class="transition ease-in duration-100"
                  leave-from-class="opacity-100"
                  leave-to-class="opacity-0"
                >
                  <ListboxOptions class="listboxOptions">
                    <ListboxOption
                      as="template"
                      v-for="driver in drivers.all"
                      :key="driver.id"
                      :value="driver"
                      v-slot="{ active, selected }"
                    >
                      <li
                        :class="[
                          active ? 'bg-indigo-600 text-white' : 'text-slate-100',
                          'relative cursor-pointer select-none rounded-xl px-3 py-2'
                        ]"
                      >
                        <div class="flex items-center justify-between gap-3">
                          <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">
                            {{ driver.name }} {{ driver.surname }}
                          </span>
                          <CheckIcon v-if="selected" class="h-5 w-5 text-white" />
                        </div>
                      </li>
                    </ListboxOption>
                  </ListboxOptions>
                </transition>
              </div>
            </Listbox>

            <div v-else class="warningBox">
              <div class="flex items-start gap-3">
                <ExclamationTriangleIcon class="h-5 w-5 shrink-0 text-yellow-400" />
                <div>
                  <div class="text-sm font-medium text-yellow-200">Brak dostępnych kierowców.</div>
                  <router-link
                    class="mt-1 inline-block text-sm font-medium text-yellow-100 underline hover:text-white"
                    to="/dashboard/users"
                  >
                    Dodaj kierowcę
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- REGION -->
        <section class="sectionCard">
          <div class="sectionHead">
            <div>
              <div class="sectionTitle">Region główny</div>
              <div class="sectionSub">
                Ustal region bazowy dla tworzonej trasy.
              </div>
            </div>
          </div>

          <div class="sectionBody">
            <Listbox as="div" v-model="selectedRegion" v-if="regions.all.length > 0 && !load">
              <div class="relative">
                <ListboxButton class="listboxButton">
                  <span class="block truncate">
                    {{ selectedRegion === null ? 'Wybierz region...' : selectedRegion.name }}
                  </span>

                  <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <ChevronDownIcon class="h-5 w-5 text-slate-400" aria-hidden="true" />
                  </span>
                </ListboxButton>

                <transition
                  leave-active-class="transition ease-in duration-100"
                  leave-from-class="opacity-100"
                  leave-to-class="opacity-0"
                >
                  <ListboxOptions class="listboxOptions">
                    <ListboxOption
                      as="template"
                      v-for="region in regions.all"
                      :key="region.id"
                      :value="region"
                      v-slot="{ active, selected }"
                    >
                      <li
                        :class="[
                          active ? 'bg-indigo-600 text-white' : 'text-slate-100',
                          'relative cursor-pointer select-none rounded-xl px-3 py-2'
                        ]"
                      >
                        <div class="flex items-center justify-between gap-3">
                          <span :class="[selected ? 'font-semibold' : 'font-normal', 'block truncate']">
                            {{ region.name }}
                          </span>
                          <CheckIcon v-if="selected" class="h-5 w-5 text-white" />
                        </div>
                      </li>
                    </ListboxOption>
                  </ListboxOptions>
                </transition>
              </div>
            </Listbox>

            <div v-else class="warningBox">
              <div class="flex items-start gap-3">
                <ExclamationTriangleIcon class="h-5 w-5 shrink-0 text-yellow-400" />
                <div>
                  <div class="text-sm font-medium text-yellow-200">Brak dostępnych regionów.</div>
                  <router-link
                    class="mt-1 inline-block text-sm font-medium text-yellow-100 underline hover:text-white"
                    to="/dashboard/routes/regions"
                  >
                    Dodaj region
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- VEHICLE -->
        <section class="sectionCard">
          <div class="sectionHead">
            <div>
              <div class="sectionTitle">Pojazd</div>
              <div class="sectionSub">
                Wybierz aktywny pojazd dostępny dla tej trasy.
              </div>
            </div>
          </div>

          <div class="sectionBody">
            <Listbox as="div" v-model="selectedVehicles" v-if="vehicles.length > 0 && !load">
              <div class="relative">
                <ListboxButton class="listboxButton">
                  <span class="flex items-center gap-2 truncate">
                    <span class="statusDot bg-green-400" />
                    <span class="block truncate">
                      {{ selectedVehicles === null ? 'Wybierz pojazd...' : fullSelectedVehicle }}
                    </span>
                  </span>

                  <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <ChevronDownIcon class="h-5 w-5 text-slate-400" aria-hidden="true" />
                  </span>
                </ListboxButton>

                <transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
                  <ListboxOptions class="listboxOptions">
                    <ListboxOption
                      as="template"
                      v-for="vehicle in vehicles"
                      :key="vehicle.id"
                      :disabled="!vehicle.available || !vehicle.active"
                      :value="vehicle"
                      v-slot="{ active }"
                    >
                      <li
                        v-tooltip="`${vehicle.available ? 'Dostępny' : 'Niedostępny'}`"
                        :class="[
                          !vehicle.available || !vehicle.active ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                          active ? 'bg-indigo-600 text-white' : 'text-slate-100',
                          'relative select-none rounded-xl px-3 py-2'
                        ]"
                      >
                        <div class="flex items-center justify-between gap-3">
                          <div class="flex min-w-0 items-center gap-3">
                            <span
                              :class="[
                                vehicle.active ? (vehicle.available ? 'bg-green-400' : 'bg-red-500') : 'bg-yellow-500',
                                'statusDot'
                              ]"
                            />
                            <span class="block truncate">
                              {{ vehicle.brand }}
                              <span class="font-semibold">{{ vehicle.licensePlate }}</span>
                            </span>
                          </div>
                          <span class="shrink-0 text-xs font-semibold">
                            {{ vehicle.tankLitreCapacity }} L
                          </span>
                        </div>
                      </li>
                    </ListboxOption>
                  </ListboxOptions>
                </transition>
              </div>
            </Listbox>

            <div v-if="vehicles.length === 0" class="warningBox">
              <div class="flex items-start gap-3">
                <ExclamationTriangleIcon class="h-5 w-5 shrink-0 text-yellow-400" />
                <div>
                  <div class="text-sm font-medium text-yellow-200">Brak dostępnych pojazdów.</div>
                  <router-link
                    class="mt-1 inline-block text-sm font-medium text-yellow-100 underline hover:text-white"
                    to="/dashboard/fleet"
                  >
                    Dodaj pojazd
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </section>

        <!-- TRAILER -->
        <section class="sectionCard xl:col-span-2">
          <div class="sectionHead">
            <div>
              <div class="sectionTitle">Naczepa</div>
              <div class="sectionSub">
                Możesz przypisać naczepę lub pozostawić trasę bez naczepy.
              </div>
            </div>
          </div>

          <div class="sectionBody">
            <Listbox as="div" v-model="selectedTrails" v-if="trails.length > 0 && !load">
              <div class="relative">
                <ListboxButton class="listboxButton">
                  <span class="flex items-center gap-2 truncate">
                    <span class="statusDot bg-green-400" />
                    <span class="block truncate">
                      {{ selectedTrails === null ? 'Wybierz naczepę...' : fullSelectedTrail }}
                    </span>
                  </span>

                  <span class="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
                    <ChevronDownIcon class="h-5 w-5 text-slate-400" aria-hidden="true" />
                  </span>
                </ListboxButton>

                <transition leave-active-class="transition ease-in duration-100" leave-from-class="opacity-100" leave-to-class="opacity-0">
                  <ListboxOptions class="listboxOptions">
                    <ListboxOption
                      v-if="selectedTrails !== null"
                      as="template"
                      :value="null"
                      v-slot="{ active }"
                    >
                      <li
                        :class="[
                          active ? 'bg-indigo-600 text-white' : 'text-red-300',
                          'relative cursor-pointer select-none rounded-xl px-3 py-2'
                        ]"
                      >
                        <span class="font-semibold">Anuluj wybór</span>
                      </li>
                    </ListboxOption>

                    <ListboxOption
                      as="template"
                      v-for="trail in trails"
                      :key="trail.id"
                      :disabled="!trail.available || !trail.active"
                      :value="trail"
                      v-slot="{ active }"
                    >
                      <li
                        v-tooltip="`${trail.available ? 'Dostępny' : 'Niedostępny'}`"
                        :class="[
                          !trail.available || !trail.active ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer',
                          active ? 'bg-indigo-600 text-white' : 'text-slate-100',
                          'relative select-none rounded-xl px-3 py-2'
                        ]"
                      >
                        <div class="flex items-center justify-between gap-3">
                          <div class="flex min-w-0 items-center gap-3">
                            <span
                              :class="[
                                trail.active ? (trail.available ? 'bg-green-400' : 'bg-red-500') : 'bg-yellow-500',
                                'statusDot'
                              ]"
                            />
                            <span class="block truncate">
                              {{ trail.brand }}
                              <span class="font-semibold">{{ trail.licensePlate }}</span>
                            </span>
                          </div>
                          <span class="shrink-0 text-xs font-semibold">
                            {{ trail.tankLitreCapacity }} L
                          </span>
                        </div>
                      </li>
                    </ListboxOption>
                  </ListboxOptions>
                </transition>
              </div>
            </Listbox>

            <div v-if="trails.length === 0" class="warningBox">
              <div class="flex items-start gap-3">
                <ExclamationTriangleIcon class="h-5 w-5 shrink-0 text-yellow-400" />
                <div>
                  <div class="text-sm font-medium text-yellow-200">Brak dostępnych naczep.</div>
                  <router-link
                    class="mt-1 inline-block text-sm font-medium text-yellow-100 underline hover:text-white"
                    to="/dashboard/fleet"
                  >
                    Dodaj naczepę
                  </router-link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <!-- SUMMARY BAR — horizontal, full width -->
      <div class="border border-slate-700 bg-slate-800 bg-opacity-80 shadow-lg pageShell">
        <div class="px-4 py-3 sm:px-5">
          <div class="mb-3">
            <div class="text-xs font-semibold uppercase tracking-wide text-slate-400">Podsumowanie zestawu</div>
          </div>
          <div class="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            <div class="softBox">
              <div class="softBoxLabel">Kierowca</div>
              <div class="softBoxValue">
                {{ selectedDriver ? `${selectedDriver.name} ${selectedDriver.surname}` : '—' }}
              </div>
            </div>

            <div class="softBox">
              <div class="softBoxLabel">Region główny</div>
              <div class="softBoxValue">
                {{ selectedRegion ? selectedRegion.name : '—' }}
              </div>
            </div>

            <div class="softBox">
              <div class="softBoxLabel">Pojazd</div>
              <div class="softBoxValue">
                {{ selectedVehicles ? fullSelectedVehicle : '—' }}
              </div>
            </div>

            <div class="softBox">
              <div class="softBoxLabel">Naczepa</div>
              <div class="softBoxValue">
                {{ selectedTrails ? fullSelectedTrail : 'Brak naczepy' }}
              </div>
            </div>

            <div class="softBox">
              <div class="softBoxLabel">Łączna pojemność</div>
              <div class="softBoxValue" :class="countCapacityOfVehicleAndTrail === 0 ? 'text-red-300' : 'text-green-300'">
                {{ countCapacityOfVehicleAndTrail }} L
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-col-reverse gap-3 border-t border-slate-700 pt-4 sm:flex-row sm:items-center sm:justify-between">
        <div class="text-xs text-slate-400">
          Uzupełnij daty, region, kierowcę i zestaw transportowy, aby przejść dalej.
        </div>

        <div class="flex items-center justify-end gap-2">
          <button
            type="button"
            :disabled="!stepValidation"
            :class="[
              !stepValidation ? 'btnPrimary opacity-50 cursor-not-allowed' : 'btnPrimary'
            ]"
            @click="changeStep(2)"
          >
            Następny krok
          </button>
        </div>
      </div>
    </div>

    <!-- STEP 2 -->
    <div v-if="step === 2" class="space-y-4">
      <div class="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <div class="summaryCardSlim">
          <div class="summaryCardLabel">Wybrano zamówień</div>
          <div :class="['summaryCardValue', checkedOrderIds.length === 0 ? 'text-red-300' : 'text-green-300']">
            {{ checkedOrderIds.length }}
          </div>
        </div>

        <div class="summaryCardSlim">
          <div class="summaryCardLabel">Ilość litrów zamówień</div>
          <div :class="['summaryCardValue', countLitreOfPickedOrders > routeData.totalLitreCapacity ? 'text-red-300' : 'text-green-300']">
            {{ countLitreOfPickedOrders }} L
          </div>
        </div>

        <div class="summaryCardSlim">
          <div class="summaryCardLabel">Pojemność zestawu</div>
          <div class="summaryCardValue text-slate-100">
            {{ routeData.totalLitreCapacity }} L
          </div>
        </div>
      </div>

      <div class="border border-slate-700 bg-slate-800 bg-opacity-80 shadow-lg pageShell">
        <!-- TABLE HEADER with inline filter -->
        <div class="border-b border-slate-700 px-4 py-4 sm:px-5">
          <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
            <div class="min-w-0">
              <div class="text-sm font-semibold text-white">
                Zamówienia do przypisania
              </div>
              <div class="mt-1 text-xs text-slate-400">
                Wybierz zamówienia, które mają wejść do trasy.
              </div>
            </div>

            <!-- FILTER — moved into table header -->
            <div class="flex items-center gap-2">
              <label class="shrink-0 text-xs font-semibold text-slate-400">Region:</label>
              <div class="relative">
                <select
                  v-model="filters.pickedRegion"
                  @change="updateFilters()"
                  class="filterFieldInline"
                >
                  <option value="all">Wszystkie regiony</option>
                  <option
                    v-for="region in regions.all"
                    :key="region.id"
                    :value="region.name"
                  >
                    {{ region.name }}
                  </option>
                </select>
              </div>
              <button
                v-if="filters.pickedRegion !== 'all'"
                type="button"
                class="btnGhostXs"
                @click="resetFilters()"
              >
                <XMarkIcon class="h-3.5 w-3.5" />
                Resetuj
              </button>
            </div>
          </div>
        </div>

        <div class="px-4 py-4 sm:px-5">
          <div v-if="!queryLoading" class="overflow-hidden rounded-2xl border border-slate-700">
            <div class="w-full overflow-x-auto">
              <table class="min-w-[980px] w-full divide-y divide-slate-700">
                <thead class="bg-slate-700 bg-opacity-70">
                  <tr>
                    <th class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-slate-100 sm:pl-6">ID</th>
                    <th class="px-3 py-3.5 text-left text-sm font-semibold text-slate-100">Klient</th>
                    <th class="px-3 py-3.5 text-left text-sm font-semibold text-slate-100">Region dostawy</th>
                    <th class="px-3 py-3.5 text-left text-sm font-semibold text-slate-100">Pref. data dostawy</th>
                    <th class="px-3 py-3.5 text-left text-sm font-semibold text-slate-100">Produkt</th>
                    <th class="px-3 py-3.5 text-left text-sm font-semibold text-slate-100">Ilość</th>
                    <th class="py-3.5 pl-3 pr-4 text-right text-sm font-semibold text-slate-100 sm:pr-6">Wybór</th>
                  </tr>
                </thead>

                <tbody class="divide-y divide-slate-700 bg-slate-800 bg-opacity-40">
                  <tr
                    v-for="row in fuelOrders"
                    :key="row.id"
                    class="transition hover:bg-slate-700 hover:bg-opacity-30"
                  >
                    <td class="whitespace-nowrap py-4 pl-4 pr-3 text-xs sm:pl-6">
                      <span
                        v-tooltip="`${row.id}`"
                        @click.prevent="copyToClipboard(row.id)"
                        class="cursor-pointer font-semibold text-blue-300 transition hover:text-blue-200"
                      >
                        Kopiuj
                      </span>
                    </td>

                    <td class="px-3 py-4 text-xs text-slate-100">
                      {{ row.client?.invoiceAddress?.name || '—' }}
                    </td>

                    <td class="px-3 py-4 text-xs text-slate-200">
                      <div class="inline-flex items-center gap-2">
                        <span>{{ row.deliveryAddress?.regionName || 'Nie przypisano' }}</span>
                        <button
                          type="button"
                          v-tooltip="'Pokaż adres dostawy na mapie'"
                          class="inline-flex items-center justify-center h-6 w-6 rounded-lg border border-slate-600 bg-slate-700 bg-opacity-70 text-slate-300 transition hover:bg-slate-600 hover:text-white"
                          @click="openMapPreview(row.deliveryAddress)"
                        >
                          <EyeIcon class="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>

                    <td class="whitespace-nowrap px-3 py-4 text-xs text-slate-200">
                      {{
                        (row.deliveryDate?.from?.asText && row.deliveryDate?.to?.asText && row.deliveryDate.from.asText !== row.deliveryDate.to.asText)
                          ? `${row.deliveryDate.from.asText} – ${row.deliveryDate.to.asText}`
                          : (row.deliveryDate?.from?.asText || row.deliveryDate?.to?.asText || '—')
                      }}
                    </td>

                    <td class="px-3 py-4 text-xs text-slate-200">
                      {{ row.orderedFuel?.fuelName || '—' }}
                    </td>

                    <td class="whitespace-nowrap px-3 py-4 text-xs text-slate-200">
                      {{
                        Number.isFinite(Number(row.orderedFuel?.quantityLiters ?? NaN))
                          ? `${new Intl.NumberFormat('pl-PL').format(Number(row.orderedFuel?.quantityLiters))} L`
                          : '—'
                      }}
                    </td>

                    <td class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                      <div class="flex items-center justify-end">
                        <input
                          v-tooltip="`Wybierz zamówienie`"
                          :checked="isOrderChecked(row.id)"
                          @change="toggleOrder(row)"
                          type="checkbox"
                          class="mb-0.5 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-600"
                        />
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>

              <EmptyState v-if="fuelOrders.length === 0"></EmptyState>
            </div>
          </div>

          <div v-else class="flex items-center justify-center py-10">
            <div class="spinner"></div>
          </div>

          <div class="mt-5 flex items-center justify-center" v-if="!queryLimitReached && !queryLoading">
            <button
              type="button"
              @click.prevent="loadMore(10)"
              class="btnSecondary"
            >
              Załaduj więcej
            </button>
          </div>
        </div>
      </div>

      <div class="flex flex-col-reverse gap-3 border-t border-slate-700 pt-4 sm:flex-row sm:items-center sm:justify-between">
        <div class="text-xs text-slate-400">
          Objętość wybranych zamówień nie powinna przekraczać pojemności zestawu.
        </div>

        <div class="flex items-center justify-end gap-2">
          <button type="button" @click.prevent="changeStep(1)" class="btnSecondary">
            Poprzedni krok
          </button>

          <button
            type="button"
            :disabled="!stepValidation"
            :class="[!stepValidation ? 'btnPrimary opacity-50 cursor-not-allowed' : 'btnPrimary']"
            @click="changeStep(3)"
          >
            Zapisz trasę
          </button>
        </div>
      </div>
    </div>

    <!-- STEP 3 -->
    <div v-show="step === 3" class="space-y-4">
      <div class="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <div class="xl:col-span-1">
          <section class="sectionCard">
            <div class="sectionHead">
              <div>
                <div class="sectionTitle">Nazwa trasy</div>
                <div class="sectionSub">
                  Domyślna nazwa została już uzupełniona, ale możesz ją zmienić.
                </div>
              </div>
            </div>

            <div class="sectionBody">
              <div class="fieldWrap">
                <label class="fieldLabel">Nazwa</label>
                <input
                  v-model="routeData.name"
                  type="text"
                  class="fieldInput"
                  placeholder="np. Trasa"
                />
              </div>
            </div>
          </section>
        </div>

        <div class="xl:col-span-2">
          <section class="sectionCard">
            <div class="sectionHead">
              <div>
                <div class="sectionTitle">Notatki</div>
                <div class="sectionSub">
                  Dodaj dodatkowe informacje organizacyjne dla trasy.
                </div>
              </div>
            </div>

            <div class="sectionBody">
              <div class="fieldWrap">
                <label class="fieldLabel">Notatki</label>
                <textarea
                  v-model="routeData.note"
                  rows="5"
                  class="fieldInput fieldTextarea"
                  placeholder="np. Notatki"
                ></textarea>
              </div>
            </div>
          </section>
        </div>
      </div>

      <div class="flex flex-col-reverse gap-3 border-t border-slate-700 pt-4 sm:flex-row sm:items-center sm:justify-between">
        <div class="text-xs text-slate-400">
          Ostatni krok — zapisz nazwę i notatki albo pozostaw wartości domyślne.
        </div>

        <div class="flex items-center justify-end gap-2">
          <button
            type="button"
            :disabled="!lastStepValidation"
            :class="[!lastStepValidation ? 'btnPrimary opacity-50 cursor-not-allowed' : 'btnPrimary']"
            @click="confirmRoute()"
          >
            Zapisz nazwę i notatki
          </button>
        </div>
      </div>
    </div>

    <!-- DEBUG -->
    <details class="sectionCard" v-if="step === 3">
      <summary class="sectionHead cursor-pointer select-none list-none">
        <div>
          <div class="sectionTitle">Podgląd danych trasy</div>
          <div class="sectionSub">
            Techniczny podgląd obiektu przed końcowym użyciem.
          </div>
        </div>
      </summary>

      <div class="sectionBody">
        <pre class="debugBox">{{ routeDataSafe }}</pre>
      </div>
    </details>

    <!-- Map preview modal (shared) -->
    <ModalMapPreview
      :show="mapPreviewModal.show"
      :delivery-address="mapPreviewModal.deliveryAddress"
      @close="closeMapPreview"
    />
  </div>
</template>

<script>
import { db } from "@/firebase/gfbconf.js"
import {
  HomeIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  CheckIcon,
  ChevronRightIcon
} from '@heroicons/vue/24/solid'
import {
  ExclamationTriangleIcon,
  XMarkIcon,
  FunnelIcon,
  ArrowLeftIcon,
  EyeIcon
} from '@heroicons/vue/24/outline'
import { ListboxOptions, ListboxOption, ListboxButton, Listbox } from '@headlessui/vue'
import VueDatePicker from '@vuepic/vue-datepicker'
import EmptyState from '@/components/EmptyState.vue'
import ModalMapPreview from '@/components/ModalMapPreview.vue'
import moment from 'moment'
import '@vuepic/vue-datepicker/dist/main.css'

export default {
  name: "Template",
  data() {
    return {
      load: false,
      progress: 0,
      step: 1,
      drivers: {
        all: [],
        input: {
          showList: false,
          query: ""
        }
      },
      selectedDriver: null,
      regions: {
        all: [],
        selected: null
      },
      selectedRegion: null,
      vehicles: [],
      selectedVehicles: null,
      vehicleOpen: false,
      trails: [],
      selectedTrails: null,
      trailOpen: false,
      date: null,
      routeData: {
        allRegions: [],
        archive: false,
        driver: null,
        id: null,
        mainRegion: null,
        name: '',
        note: '',
        orders: [],
        ordersAmount: 0,
        plannedEnd: null,
        plannedStart: null,
        status: 1,
        totalLitreCapacity: 0,
        trailer: null,
        vehicle: null
      },
      filters: {
        showFilters: false,
        pickedRegion: 'all',
      },
      fuelOrders: [],
      dbListener: null,
      query: null,
      queryLoading: false,
      queryLimit: 20,
      queryLimitReached: false,
      checkedOrderIds: [],
      selectedOrdersById: {},
      mapPreviewModal: {
        show: false,
        deliveryAddress: null
      },
    }
  },
  components: {
    EmptyState,
    FunnelIcon,
    HomeIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    ChevronRightIcon,
    CheckIcon,
    ExclamationTriangleIcon,
    XMarkIcon,
    ArrowLeftIcon,
    EyeIcon,
    Listbox,
    ListboxButton,
    ListboxOption,
    ListboxOptions,
    VueDatePicker,
    ModalMapPreview
  },
  mounted() {
    this.getDrivers()
    this.getVehicles()
    this.getTrails()
    this.getRegions()
  },
  watch: {
    countCapacityOfVehicleAndTrail(newVal) {
      this.routeData.totalLitreCapacity = newVal
    },
    date(newVal) {
      if (newVal && newVal[0] && newVal[1]) {
        this.routeData.plannedStart = {
          date: moment(newVal[0]).toDate(),
          unix: moment(newVal[0]).unix()
        }
        this.routeData.plannedEnd = {
          date: moment(newVal[1]).toDate(),
          unix: moment(newVal[1]).unix()
        }
      } else {
        this.routeData.plannedStart = null
        this.routeData.plannedEnd = null
      }
    },
    selectedDriver(newVal) {
      if (newVal) {
        const hardCopy = JSON.parse(JSON.stringify(newVal))
        this.routeData.driver = {
          id: hardCopy.id,
          name: hardCopy.name + ' ' + hardCopy.surname,
          phone: hardCopy.contact?.phone || '',
          email: hardCopy.contact?.email || '',
          ref: db.collection("WorkersData").doc(hardCopy.id)
        }
      } else {
        this.routeData.driver = null
      }
    },
    selectedRegion(newVal) {
      if (newVal) {
        this.routeData.mainRegion = {
          id: newVal.id,
          name: newVal.name
        }
        this.filters.pickedRegion = newVal.name
      } else {
        this.routeData.mainRegion = null
      }
    },
    selectedVehicles(newVal) {
      if (newVal) {
        const hardCopy = JSON.parse(JSON.stringify(newVal))
        this.routeData.vehicle = {
          id: hardCopy.id,
          brand: hardCopy.brand,
          model: hardCopy.model,
          licensePlate: hardCopy.licensePlate,
          chambers: hardCopy.chambers,
          tankLitreCapacity: hardCopy.tankLitreCapacity,
          roadPermissionNumber: hardCopy.roadPermissionNumber || '',
          gpsTrackerNumber: hardCopy.gpsTrackerNumber || '',
          containsProduct: {
            id: 30,
            name: "Shell FuelSave Diesel CN 27102011",
          },
          ref: db.collection("Fleet").doc(hardCopy.id)
        }
      } else {
        this.routeData.vehicle = null
      }
    },
    selectedTrails(newVal) {
      if (newVal) {
        const hardCopy = JSON.parse(JSON.stringify(newVal))
        this.routeData.trailer = {
          id: hardCopy.id,
          brand: hardCopy.brand,
          model: hardCopy.model,
          licensePlate: hardCopy.licensePlate,
          chambers: hardCopy.chambers,
          tankLitreCapacity: hardCopy.tankLitreCapacity,
          containsProduct: {
            id: 30,
            name: "Shell FuelSave Diesel CN 27102011",
          },
          ref: db.collection("Fleet").doc(hardCopy.id)
        }
      } else {
        this.routeData.trailer = null
      }
    },
  },
  methods: {
    openMapPreview(deliveryAddress) {
      this.mapPreviewModal.deliveryAddress = deliveryAddress || null
      this.mapPreviewModal.show = true
    },
    closeMapPreview() {
      this.mapPreviewModal.show = false
      setTimeout(() => { this.mapPreviewModal.deliveryAddress = null }, 200)
    },
    copyToClipboard(text) {
      if (!text) return
      navigator.clipboard.writeText(text).then(() => {
        this.$store.commit('setNotification', {
          show: true,
          head: 'Skopiowano do schowka!',
          subheader: '',
          success: true
        })
      })
    },

    resetFilters() {
      this.filters.pickedRegion = 'all'
      this.updateFilters()
    },

    loadMore(amount) {
      if (this.query) {
        this.query
          .limit(this.checkedOrderIds.length + amount)
          .get()
          .then((querySnapshot) => {
            this.fuelOrders = []

            this.queryLimitReached = querySnapshot.docs.length !== 0
              ? querySnapshot.docs.length < (this.checkedOrderIds.length + amount)
              : false

            querySnapshot.forEach((doc) => {
              const data = doc.data()
              data.id = doc.id
              data.activeSelected = false
              this.fuelOrders.push(data)
            })
          })
          .catch((err) => {
            console.error("FuelOrders snapshot error:", err)
          })
      }
    },

    isOrderChecked(id) {
      return this.checkedOrderIds.includes(id)
    },

    toggleOrder(order) {
      const id = order.id
      const exists = this.checkedOrderIds.includes(id)

      if (!exists) {
        this.checkedOrderIds.push(id)

        this.selectedOrdersById[id] = {
          arrivalUnix: null,
          id,
          ref: db.collection("FuelOrders").doc(id),
          snapshotAtAddingTime: {
            addingTimeUnix: moment().unix(),
            companyName: order.client?.invoiceAddress?.name || '—',
            deliveryAddress: {
              city: order.deliveryAddress?.city || '—',
              postCode: order.deliveryAddress?.postCode || '—',
              regionName: order.deliveryAddress?.regionName || 'Nie przypisano',
              address: order.deliveryAddress?.address || '—',
              coordinates: order.deliveryAddress?.coordinates || null
            },
            orderedFuel: {
              fuelId: order.orderedFuel?.fuelId || '—',
              fuelName: order.orderedFuel?.fuelName || '—',
              quantityLiters: order.orderedFuel?.quantityLiters || '—'
            },
          },
          status: "planned"
        }
      } else {
        this.checkedOrderIds = this.checkedOrderIds.filter(x => x !== id)
        delete this.selectedOrdersById[id]
      }

      this.rebuildRouteOrders()
    },

    rebuildRouteOrders() {
      const orders = this.checkedOrderIds
        .map((id, idx) => {
          const o = this.selectedOrdersById[id]
          if (!o) return null
          return { ...o, sortIndex: idx }
        })
        .filter(Boolean)

      const regionsWithoutDuplicates = [...new Set(orders.map(o => o.snapshotAtAddingTime.deliveryAddress.regionName))]
      this.routeData.allRegions = regionsWithoutDuplicates
      this.routeData.orders = orders
      this.routeData.ordersAmount = orders.length
    },

    updateFilters() {
      if (this.dbListener) {
        this.dbListener()
      }
      this.getFuelOrders()
    },

    toggleFilters() {
      this.filters.showFilters = !this.filters.showFilters
    },

    todayAt00() {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return today
    },

    async getFuelOrders() {
      if (this.dbListener !== null) {
        this.dbListener()
      }

      this.query = db.collection("FuelOrders")

      if (this.filters.pickedRegion !== "all") {
        this.query = this.query.where('deliveryAddress.regionName', "==", this.filters.pickedRegion)
      }

      this.query = this.query
        .where('status', "==", 2)
        .orderBy("deliveryDate.to.unix", "asc")

      this.dbListener = this.query
        .limit(this.queryLimit)
        .onSnapshot(
          (querySnapshot) => {
            this.queryLoading = true
            this.fuelOrders = []

            this.queryLimitReached = querySnapshot.docs.length !== 0
              ? querySnapshot.docs.length < this.queryLimit
              : false

            querySnapshot.forEach((doc) => {
              const data = doc.data()
              data.id = doc.id
              data.activeSelected = false
              this.fuelOrders.push(data)
            })

            this.queryLoading = false
          },
          (err) => {
            console.error("FuelOrders snapshot error:", err)
            this.queryLoading = false
          }
        )
    },

    async getVehicles() {
      try {
        this.load = true

        const querySnapshot = await db
          .collection("Fleet")
          .where("vehicle", "==", true)
          .orderBy("meta.created.unix", "desc")
          .get()

        const vehicles = []

        querySnapshot.forEach((doc) => {
          const data = doc.data()
          data.id = doc.id
          data.available = data.active !== false
          vehicles.push(data)
        })

        vehicles.sort((a, b) => {
          if (a.available && !b.available) return -1
          if (!a.available && b.available) return 1
          return 0
        })

        this.vehicles = vehicles
      } catch (error) {
        console.log(error)
        this.$store.commit("setNotification", {
          show: true,
          head: "Wystąpił nieoczekiwany błąd!",
          subheader: ``,
          success: false,
        })
      } finally {
        this.load = false
      }
    },

    async getTrails() {
      try {
        this.load = true

        const querySnapshot = await db
          .collection("Fleet")
          .where("vehicle", "==", false)
          .orderBy("meta.created.unix", "desc")
          .get()

        const trails = []

        querySnapshot.forEach((doc) => {
          const data = doc.data()
          data.id = doc.id
          data.available = data.active !== false
          trails.push(data)
        })

        trails.sort((a, b) => {
          if (a.available && !b.available) return -1
          if (!a.available && b.available) return 1
          return 0
        })

        this.trails = trails
      } catch (error) {
        console.log(error)
        this.$store.commit("setNotification", {
          show: true,
          head: "Wystąpił nieoczekiwany błąd!",
          subheader: ``,
          success: false,
        })
      } finally {
        this.load = false
      }
    },

    async getDrivers() {
      try {
        this.drivers.all = []
        const driversDoc = await db.collection("WorkersData").where("permissions.driver", "==", true).orderBy("name", "asc").get()

        driversDoc.forEach(driverDoc => {
          const driver = driverDoc.data()
          this.drivers.all.push({
            id: driverDoc.id,
            ...driver
          })
        })
      } catch (error) {
        console.log(error)
        this.$store.commit('setNotification', {
          show: true,
          head: "Wystąpił nieoczekiwany błąd!",
          subheader: ``,
          success: false
        })
      }
    },

    async getRegions() {
      try {
        this.regions.all = []
        const regionsDoc = await db.collection("routeRegions").doc("regions").get()

        if (regionsDoc.exists) {
          const regionsData = regionsDoc.data()
          this.regions.all = regionsData.regions || []
        }
      } catch (error) {
        console.log(error)
        this.$store.commit('setNotification', {
          show: true,
          head: "Wystąpił nieoczekiwany błąd!",
          subheader: ``,
          success: false
        })
      }
    },

    async confirmRoute() {
      try {
        if (this.routeData.name.length > 0 && this.routeData.note.length > 0) {
          await db.collection('Routes').doc(this.routeData.id).update({
            note: this.routeData.note,
            name: this.routeData.name,
          })
          this.$store.commit('setNotification', {
            show: true,
            head: "Akcja zakończona sukcesem!",
            subheader: `Dodatkowe rzeczy zostały pomyślnie ustawione.`,
            success: true
          })
        } else if (this.routeData.note.length > 0) {
          await db.collection('Routes').doc(this.routeData.id).update({
            note: this.routeData.note,
          })
          this.$store.commit('setNotification', {
            show: true,
            head: "Opis trasy pomyślnie ustawiony.",
            subheader: ``,
            success: true
          })
        } else if (this.routeData.name.length > 0) {
          await db.collection('Routes').doc(this.routeData.id).update({
            name: this.routeData.name,
          })
          this.$store.commit('setNotification', {
            show: true,
            head: "Tytuł trasy pomyślnie ustawiony.",
            subheader: ``,
            success: true
          })
        } else {
          this.$store.commit('setNotification', {
            show: true,
            head: "Trasa pomyślnie utworzona.",
            subheader: ``,
            success: true
          })
        }

        this.$router.push(`/dashboard/routes`)
      } catch (error) {
        console.log(error)
        this.$store.commit('setNotification', {
          show: true,
          head: "Wystąpił nieoczekiwany błąd!",
          subheader: ``,
          success: false
        })
      }
    },

    async changeStep(val) {
      switch (val) {
        case 1:
          this.progress = 0
          this.step = 1
          break
        case 2:
          if (this.stepValidation === true) {
            this.step = 2
            this.progress = 50
            this.clearData()
            await this.getFuelOrders()
          }
          break
        case 3:
          this.step = 3
          this.progress = 75
          await this.addDateToDb()
          break
        case 4:
          this.step = 4
          this.progress = 100
          break
      }
    },

    validateStep() {
      if (!this.date || !this.date[0] || !this.date[1] || this.selectedDriver === null || this.selectedVehicles === null || this.countCapacityOfVehicleAndTrail === 0 || this.selectedRegion === null) return false
      return true
    },

    clearData() {
      // zachowane celowo jako soft reset kroku
    },

    async addDateToDb() {
      try {
        const dateForName = moment(this.date[0]).format('DD.MM.YYYY')
        const routeName = `${this.selectedRegion.name} - ${this.selectedDriver.name} - ${dateForName}`
        this.routeData.name = routeName

        const routeRef = await db.collection("Routes").add(this.routeData)
        await routeRef.update({ id: routeRef.id })
        this.routeData.id = routeRef.id

        const batch = db.batch()
        this.routeData.orders.forEach(order => {
          const orderRef = db.collection("FuelOrders").doc(order.id)
          batch.update(orderRef, {
            status: 3,
            isInRoute: true,
            routeRef: db.collection("Routes").doc(routeRef.id),
            deliveryAttempted: false
          })
        })
        await batch.commit()
      } catch (error) {
        console.error("Error adding route: ", error)
        this.$store.commit('setNotification', {
          show: true,
          head: "Wystąpił nieoczekiwany błąd podczas zapisywania trasy!",
          subheader: ``,
          success: false
        })
      }
    },

    validate() {
      if (!this.date || !this.date[0] || !this.date[1]) return false
      return true
    },

    periodDateFormat() {
      let date = ''
      if (this.date) {
        date = `${moment(this.date[0]).format('DD.MM.YYYY HH:mm')} - ${moment(this.date[1]).format('DD.MM.YYYY HH:mm')}`
      }
      return date
    },
  },
  computed: {
    countLitreOfPickedOrders() {
      let totalLiters = 0
      for (const order of this.routeData.orders) {
        const quantity = Number(order.snapshotAtAddingTime.orderedFuel.quantityLiters)
        if (Number.isFinite(quantity)) {
          totalLiters += quantity
        }
      }
      return totalLiters
    },

    routeDataSafe() {
      const rd = this.routeData || {}
      const d = rd.driver || null
      const v = rd.vehicle || null
      const t = rd.trailer || null

      return {
        ...rd,
        driver: d
          ? {
              id: d.id,
              name: d.name,
              phone: d.phone,
              email: d.email,
              ref: 'FOR VISUAL DISPLAY: ' + (d.ref?.path || null),
            }
          : null,
        vehicle: v
          ? {
              id: v.id,
              brand: v.brand,
              model: v.model,
              licensePlate: v.licensePlate,
              tankLitreCapacity: v.tankLitreCapacity,
              containsProduct: v.containsProduct,
              ref: 'FOR VISUAL DISPLAY: ' + (v.ref?.path || null),
            }
          : null,
        trailer: t
          ? {
              id: t.id,
              brand: t.brand,
              model: t.model,
              licensePlate: t.licensePlate,
              tankLitreCapacity: t.tankLitreCapacity,
              containsProduct: t.containsProduct,
              ref: 'FOR VISUAL DISPLAY: ' + (t.ref?.path || null),
            }
          : null,
        orders: Array.isArray(rd.orders)
          ? rd.orders.map(o => ({
              ...o,
              ref: 'FOR VISUAL DISPLAY: ' + (o.ref?.path || null),
            }))
          : []
      }
    },

    fullSelectedVehicle() {
      if (!this.selectedVehicles) return ''
      return `${this.selectedVehicles.brand} ${this.selectedVehicles.model} [${this.selectedVehicles.licensePlate}]`
    },

    fullSelectedTrail() {
      if (!this.selectedTrails) return ''
      return `${this.selectedTrails.brand} ${this.selectedTrails.model} [${this.selectedTrails.licensePlate}]`
    },

    lastStepValidation() {
      if (this.routeData.name.length > 0 && this.routeData.note.length > 0) return true
      if (this.routeData.note.length > 0) return true
      if (this.routeData.name.length > 0) return true
      return false
    },

    stepValidation() {
      return this.validateStep()
    },

    validation() {
      return this.validate()
    },

    countCapacityOfVehicleAndTrail() {
      let totalCapacity = 0
      if (this.selectedVehicles && this.selectedVehicles.tankLitreCapacity) {
        totalCapacity += this.selectedVehicles.tankLitreCapacity
      }
      if (this.selectedTrails && this.selectedTrails.tankLitreCapacity) {
        totalCapacity += this.selectedTrails.tankLitreCapacity
      }
      return totalCapacity
    }
  }
}
</script>

<style scoped>
.pageShell {
  border-radius: 26px;
}

.sectionCard {
  overflow: visible;
  position: relative;
  border: 1px solid rgba(71, 85, 105, 0.85);
  border-radius: 22px;
  background: rgba(30, 41, 59, 0.72);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
}

.sectionHead {
  padding: 18px 18px 14px;
  border-bottom: 1px solid rgba(71, 85, 105, 0.65);
}

.sectionTitle {
  font-size: 15px;
  line-height: 1.3;
  font-weight: 700;
  color: #f8fafc;
}

.sectionSub {
  margin-top: 4px;
  font-size: 12px;
  line-height: 1.45;
  color: #94a3b8;
}

.sectionBody {
  padding: 16px 18px 18px;
}

.stepPill {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 9999px;
  border: 1px solid rgba(71, 85, 105, 0.85);
  background: rgba(51, 65, 85, 0.55);
  font-size: 11px;
  line-height: 1;
  color: #94a3b8;
  font-weight: 600;
}

.stepPillActive {
  color: #ffffff;
  background: rgba(37, 99, 235, 0.2);
  border-color: rgba(59, 130, 246, 0.45);
}

/* Inline filter in table header */
.filterFieldInline {
  display: block;
  min-height: 36px;
  border-radius: 10px;
  border: 1px solid rgba(100, 116, 139, 0.65);
  background: rgba(15, 23, 42, 0.7);
  color: #f8fafc;
  padding: 7px 32px 7px 12px;
  font-size: 13px;
  line-height: 1.3;
  outline: none;
  appearance: none;
  transition: border-color 180ms ease, box-shadow 180ms ease;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3E%3Cpath stroke='%2394a3b8' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 8px center;
  background-size: 16px 16px;
}

.filterFieldInline:focus {
  border-color: rgba(99, 102, 241, 0.85);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

/* Tiny ghost button for reset */
.btnGhostXs {
  min-height: 36px;
  padding: 0 10px;
  border-radius: 10px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  font-size: 12px;
  font-weight: 600;
  border: 1px dashed rgba(100, 116, 139, 0.65);
  background: transparent;
  color: #cbd5e1;
  transition: 180ms ease;
  white-space: nowrap;
}

.btnGhostXs:hover {
  background: rgba(51, 65, 85, 0.4);
}

.fieldWrap {
  display: flex;
  flex-direction: column;
}

.fieldLabel {
  display: block;
  margin-bottom: 7px;
  font-size: 12px;
  font-weight: 600;
  color: #e2e8f0;
}

.fieldInput,
.listboxButton {
  display: block;
  width: 100%;
  min-height: 44px;
  border-radius: 14px;
  border: 1px solid rgba(100, 116, 139, 0.75);
  background: rgba(15, 23, 42, 0.7);
  color: #f8fafc;
  padding: 11px 14px;
  font-size: 14px;
  line-height: 1.3;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.03);
  outline: none;
  transition: border-color 180ms ease, box-shadow 180ms ease, background 180ms ease;
}

.listboxButton {
  position: relative;
  padding-right: 42px;
  text-align: left;
}

.fieldInput:focus,
.listboxButton:focus {
  border-color: rgba(99, 102, 241, 0.85);
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15);
}

.fieldTextarea {
  min-height: 120px;
  resize: vertical;
}

.listboxOptions {
  position: absolute;
  z-index: 60;
  margin-top: 8px;
  width: 100%;
  max-height: 240px;
  overflow-y: auto;
  border-radius: 16px;
  border: 1px solid rgba(71, 85, 105, 0.85);
  background: rgba(15, 23, 42, 0.98);
  padding: 8px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.35);
}

.warningBox {
  border-radius: 16px;
  border: 1px solid rgba(251, 191, 36, 0.35);
  background: rgba(120, 53, 15, 0.22);
  padding: 14px;
}

.statusDot {
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  flex-shrink: 0;
}

.softBox {
  border-radius: 14px;
  border: 1px solid rgba(100, 116, 139, 0.35);
  background: rgba(15, 23, 42, 0.28);
  padding: 10px 14px;
}

.softBoxLabel {
  font-size: 11px;
  font-weight: 600;
  color: rgba(148, 163, 184, 1);
}

.softBoxValue {
  margin-top: 5px;
  font-size: 13px;
  font-weight: 700;
  color: rgba(226, 232, 240, 1);
  line-height: 1.5;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.summaryCardSlim {
  border: 1px solid rgba(71, 85, 105, 0.85);
  border-radius: 22px;
  background: rgba(30, 41, 59, 0.72);
  padding: 16px 18px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.18);
}

.summaryCardLabel {
  font-size: 11px;
  font-weight: 600;
  color: #94a3b8;
}

.summaryCardValue {
  margin-top: 8px;
  font-size: 22px;
  font-weight: 800;
  line-height: 1.1;
}

.debugBox {
  margin: 0;
  border-radius: 16px;
  border: 1px solid rgba(71, 85, 105, 0.7);
  background: rgba(15, 23, 42, 0.45);
  padding: 14px;
  color: #cbd5e1;
  font-size: 12px;
  line-height: 1.5;
  overflow: auto;
  white-space: pre-wrap;
  word-break: break-word;
}

.btnSecondary,
.btnPrimary,
.btnGhost {
  min-height: 40px;
  padding: 0 16px;
  border-radius: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-size: 13px;
  font-weight: 600;
  transition: 180ms ease;
}

.btnSecondary {
  border: 1px solid rgba(100, 116, 139, 0.75);
  background: rgba(51, 65, 85, 0.8);
  color: #e2e8f0;
}

.btnSecondary:hover {
  background: rgba(71, 85, 105, 0.95);
}

.btnPrimary {
  border: 1px solid rgba(59, 130, 246, 0.5);
  background: rgba(37, 99, 235, 0.2);
  color: #dbeafe;
}

.btnPrimary:hover {
  background: rgba(37, 99, 235, 0.3);
}

.btnGhost {
  border: 1px dashed rgba(100, 116, 139, 0.65);
  background: transparent;
  color: #cbd5e1;
}

.btnGhost:hover:not(:disabled) {
  background: rgba(51, 65, 85, 0.4);
}

.btnGhost:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.25s ease, transform 0.25s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

.spinner {
  width: 1.4rem;
  height: 1.4rem;
  border-radius: 50%;
  border: 5px solid #475569;
  animation: spinner-bulqg1 1.3s infinite linear alternate, spinner-oaa3wk 2.6s infinite linear;
}

@keyframes spinner-bulqg1 {
  0% { clip-path: polygon(50% 50%, 0 0, 50% 0%, 50% 0%, 50% 0%, 50% 0%, 50% 0%); }
  12.5% { clip-path: polygon(50% 50%, 0 0, 50% 0%, 100% 0%, 100% 0%, 100% 0%, 100% 0%); }
  25% { clip-path: polygon(50% 50%, 0 0, 50% 0%, 100% 0%, 100% 100%, 100% 100%, 100% 100%); }
  50% { clip-path: polygon(50% 50%, 0 0, 50% 0%, 100% 0%, 100% 100%, 50% 100%, 0% 100%); }
  62.5% { clip-path: polygon(50% 50%, 100% 0, 100% 0%, 100% 0%, 100% 100%, 50% 100%, 0% 100%); }
  75% { clip-path: polygon(50% 50%, 100% 100%, 100% 100%, 100% 100%, 100% 100%, 50% 100%, 0% 100%); }
  100% { clip-path: polygon(50% 50%, 50% 100%, 50% 100%, 50% 100%, 50% 100%, 50% 100%, 0% 100%); }
}

@keyframes spinner-oaa3wk {
  0% { transform: scaleY(1) rotate(0deg); }
  49.99% { transform: scaleY(1) rotate(135deg); }
  50% { transform: scaleY(-1) rotate(0deg); }
  100% { transform: scaleY(-1) rotate(-135deg); }
}

/* DATEPICKER */
.datePickerWrap {
  position: relative;
  z-index: 80;
}

:deep(.dp__main) {
  width: 100%;
}

:deep(.dp__input_wrap) {
  width: 100%;
}

:deep(.dp__input) {
  width: 100%;
  min-height: 44px;
  border-radius: 14px;
  border: 1px solid rgba(100, 116, 139, 0.75);
  background: rgba(15, 23, 42, 0.7);
  color: #f8fafc;
  padding: 11px 36px 11px 38px;
  font-size: 14px;
  line-height: 1.3;
  box-shadow: inset 0 1px 0 rgba(255,255,255,0.03);
}

:deep(.dp__input::placeholder) {
  color: rgba(148, 163, 184, 0.75);
}

:deep(.dp__input_focus) {
  border-color: rgba(99, 102, 241, 0.85) !important;
  box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.15) !important;
}

:deep(.dp__icon),
:deep(.dp__clear_icon) {
  color: rgba(148, 163, 184, 0.9);
}

:deep(.dp__menu) {
  z-index: 120 !important;
  margin-top: 8px;
  border-radius: 16px;
  border: 1px solid rgba(71, 85, 105, 0.85);
  background: rgba(15, 23, 42, 0.98) !important;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.35);
  overflow: hidden;
}

:deep(.dp__menu_inner),
:deep(.dp__instance_calendar),
:deep(.dp__calendar),
:deep(.dp__calendar_wrap),
:deep(.dp__overlay),
:deep(.dp__selection_preview),
:deep(.dp__time_picker),
:deep(.dp__time_picker_inline_container),
:deep(.dp__time_picker_overlay_container),
:deep(.dp__time_picker_overlay),
:deep(.dp__action_row),
:deep(.dp__month_year_row),
:deep(.dp__calendar_header),
:deep(.dp__calendar_header_item),
:deep(.dp__month_year_wrap) {
  background: rgba(15, 23, 42, 0.98) !important;
  color: #f8fafc !important;
}

:deep(.dp__arrow_top) {
  display: none !important;
}

:deep(.dp__month_year_select) {
  color: #f8fafc !important;
}

/* Fix: white hover on calendar/time switch buttons */
:deep(.dp__btn),
:deep(.dp__icon_btn),
:deep(.dp__inner_nav),
:deep(.dp__inc_dec_button),
:deep(.dp__pm_am_button),
:deep(.dp__overlay_action),
:deep(.dp__tp_inline_btn),
:deep([data-test="tp-btn"]),
:deep(.dp__time_picker_inline_btn) {
  color: #cbd5e1 !important;
  background: transparent !important;
  border: none !important;
}

:deep(.dp__btn:hover),
:deep(.dp__icon_btn:hover),
:deep(.dp__inner_nav:hover),
:deep(.dp__inc_dec_button:hover),
:deep(.dp__pm_am_button:hover),
:deep(.dp__overlay_action:hover),
:deep(.dp__tp_inline_btn:hover),
:deep([data-test="tp-btn"]:hover),
:deep(.dp__time_picker_inline_btn:hover) {
  background: rgba(51, 65, 85, 0.9) !important;
  color: #ffffff !important;
  border: none !important;
}

:deep(.dp__week_day) {
  color: rgba(148, 163, 184, 1) !important;
  font-weight: 600;
}

:deep(.dp__calendar_item) {
  color: #e2e8f0 !important;
}

:deep(.dp__cell_inner) {
  border-radius: 10px !important;
  color: inherit !important;
  box-shadow: none !important;
  border: 0 !important;
}

:deep(.dp__cell_inner:hover) {
  background: rgba(51, 65, 85, 0.9) !important;
  color: #ffffff !important;
}

:deep(.dp__range_start),
:deep(.dp__range_end),
:deep(.dp__active_date) {
  background: rgba(37, 99, 235, 0.95) !important;
  color: #ffffff !important;
  border-radius: 10px !important;
  border: 0 !important;
  box-shadow: none !important;
}

:deep(.dp__range_between) {
  background: rgba(37, 99, 235, 0.18) !important;
  color: #dbeafe !important;
  border: 0 !important;
  box-shadow: none !important;
}

:deep(.dp__range_between .dp__cell_inner) {
  background: transparent !important;
  color: #dbeafe !important;
  border: 0 !important;
  box-shadow: none !important;
  border-radius: 0 !important;
}

:deep(.dp__range_between:hover),
:deep(.dp__range_between:hover .dp__cell_inner),
:deep(.dp__range_between.dp__date_hover),
:deep(.dp__range_between.dp__date_hover .dp__cell_inner) {
  background: rgba(37, 99, 235, 0.24) !important;
  color: #ffffff !important;
  border: 0 !important;
  box-shadow: none !important;
}

:deep(.dp__today) {
  border: 1px solid rgba(59, 130, 246, 0.65) !important;
}

:deep(.dp__cell_disabled),
:deep(.dp__disabled),
:deep(.dp__disabled .dp__cell_inner),
:deep(.dp__calendar_item.dp__cell_disabled .dp__cell_inner) {
  color: rgba(100, 116, 139, 0.6) !important;
  opacity: 0.45 !important;
  cursor: not-allowed !important;
}

:deep(.dp__time_display),
:deep(.dp__time_col),
:deep(.dp__time_input),
:deep(.dp__time_input input),
:deep(.dp__overlay_cell),
:deep(.dp__overlay_col),
:deep(.dp__overlay_cell_active),
:deep(.dp__overlay_cell_pad),
:deep(.dp__time_picker_item),
:deep(.dp__time_col_reg),
:deep(.dp__time_col_reg_block),
:deep(.dp__time_col_reg_inline) {
  color: #f8fafc !important;
  background: transparent !important;
}

:deep(.dp__time_display) {
  font-weight: 700 !important;
}

:deep(.dp__overlay_cell:hover),
:deep(.dp__overlay_col:hover),
:deep(.dp__time_display:hover),
:deep(.dp__time_col_reg_block:hover),
:deep(.dp__time_col_reg_inline:hover) {
  background: rgba(51, 65, 85, 0.9) !important;
  color: #ffffff !important;
}

:deep(.dp__action_row) {
  border-top: 1px solid rgba(71, 85, 105, 0.7) !important;
}

:deep(.dp__action_button) {
  border-radius: 12px !important;
}

:deep(.dp__action_select) {
  background: rgba(37, 99, 235, 0.95) !important;
  color: #ffffff !important;
}

:deep(.dp__action_cancel) {
  color: #cbd5e1 !important;
  background: transparent !important;
}

:deep(.dp__action_cancel:hover) {
  background: rgba(51, 65, 85, 0.9) !important;
  color: #ffffff !important;
}

:deep(.dp__selection_preview) {
  color: #94a3b8 !important;
}

/* Catch-all for any remaining white-background hover states in the picker */
:deep(.dp__menu *:hover) {
  color: inherit;
}

:deep(.dp__menu button:hover),
:deep(.dp__menu [role="button"]:hover) {
  background: rgba(51, 65, 85, 0.9) !important;
  color: #ffffff !important;
}

@media (max-width: 767px) {
  .sectionHead,
  .sectionBody {
    padding-left: 14px;
    padding-right: 14px;
  }

  .summaryCardValue {
    font-size: 18px;
  }

  .softBoxValue {
    font-size: 12px;
  }
}
</style>